<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue';
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axiosClient from '../axios'
import type { Faq } from '../types'
import { Button } from '@/components/ui/button'

const router = useRouter()
const faqs = ref<Faq[]>([])
const loading = ref(true)

onMounted(async () => {
    await fetchFaqs()
})

async function fetchFaqs() {
    loading.value = true
    try {
        const response = await axiosClient.get('/faqs/getAllFaqs')
        if (response.data && response.data.success) {
            faqs.value = response.data.data || []
        }
    } catch (error) {
        console.error('Error fetching FAQs:', error)
    } finally {
        loading.value = false
    }
}

function goToAddFaq() {
    router.push({ name: 'Pridať FAQ' })
}

function goToDetailFaq(id: number) {
    router.push({ name: 'Detail FAQ', params: { id: id.toString() } })
}

function goToEditFaq(id: number) {
    router.push({ name: 'Upraviť FAQ', params: { id: id.toString() } })
}

async function deleteFaq(id: number) {
    if (!confirm('Naozaj chcete vymazať túto FAQ?')) {
        return
    }
    try {
        const response = await axiosClient.delete(`/faqs/${id}/delete`)
        if (response.data && response.data.success) {
            await fetchFaqs()
        }
    } catch (error) {
        console.error('Error deleting FAQ:', error)
    }
}
</script>

<template>
    <main class="flex-1 overflow-y-auto focus:outline-none">
        <div class="py-6">
            <div class="max-w-(--breakpoint-2xl) mx-auto">
                <PageBreadcrumb page-title="Zoznam FAQ" />
            </div>
            <div class="max-w-(--breakpoint-2xl) mx-auto">
                <div class="bg-white shadow-xs overflow-hidden sm:rounded-lg dark:bg-gray-900">
                    <div class="px-4 py-5 sm:px-6 flex justify-between">
                        <div>
                            <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white/90">Často kladené otázky</h3>
                            <p class="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">Správa a prehľad FAQ pre zákazníkov</p>
                        </div>
                        <div>
                            <Button @click="goToAddFaq">
                                Pridať FAQ
                            </Button>
                        </div>
                    </div>
                    <div v-if="loading" class="text-center py-4">
                        <div class="mx-auto size-6 animate-spin rounded-full border-2 border-solid border-gray-300 border-t-brand-500 dark:border-gray-700">
                        </div>
                        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">Načítavanie...</p>
                    </div>
                    <div v-else-if="faqs.length === 0" class="text-center py-8">
                        <p class="text-gray-500 dark:text-gray-400">Žiadne FAQ neboli nájdené</p>
                        <Button class="mt-4" @click="goToAddFaq">
                            Pridať prvé FAQ
                        </Button>
                    </div>
                    <div v-else class="overflow-hidden">
                        <ul role="list" class="divide-y divide-gray-200 dark:divide-gray-800">
                            <li v-for="faq in faqs" :key="faq.id" class="px-6 py-4 hover:bg-gray-50 dark:hover:bg-white/5">
                                <div class="flex items-center justify-between">
                                    <div class="flex-1 min-w-0">
                                        <div class="flex items-center">
                                            <span class="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-1 mr-2 dark:bg-white/5 dark:text-gray-400">
                                                #{{ faq.position || '—' }}
                                            </span>
                                            <p class="text-sm font-medium text-gray-900 truncate dark:text-white/90">{{ faq.title }}</p>
                                        </div>
                                        <p class="mt-1 text-sm text-gray-500 line-clamp-2 dark:text-gray-400">{{ faq.content }}</p>
                                    </div>
                                </div>
                                <div class="ml-4 shrink-0 flex space-x-2">
                                        <Button variant="ghost" size="sm" class="text-brand-600 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300" @click="goToDetailFaq(faq.id)">
                                            Detail
                                        </Button>
                                        <Button variant="ghost" size="sm" class="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300" @click="goToEditFaq(faq.id)">
                                            Upraviť
                                        </Button>
                                        <Button variant="ghost" size="sm" class="text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300" @click="deleteFaq(faq.id)">
                                            Vymazať
                                        </Button>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
    </main>
</template>

<style scoped>
/* Styles have been moved to global CSS file */
</style>
